import { QueryKeys } from "@/constants/query-keys";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useUpdateBodyStats } from "@/hooks/requests/body-status/use-update-body-stats";
import { useGetGoals } from "@/hooks/requests/goals/use-get-goals";
import { useUpdateGoals } from "@/hooks/requests/goals/use-update-goals";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useState } from "react";
import { StandaloneGoalModal } from "../goal-modal-select/standalone-goal-modal";
import { WeightInputModal } from "../weight-input-modal";
import { GoalInfoItem } from "./goal-info-item";

interface ProfileGoalInfoCardProps {
  className?: string;
}

export const ProfileGoalInfoCard = memo(function ProfileGoalInfoCard({
  className,
}: ProfileGoalInfoCardProps) {
  const { bodyStats, isLoading: isBodyStatsLoading } = useGetBodyStats();
  const { goals, isLoading: isGoalsLoading } = useGetGoals();
  const { updateGoalsAsync } = useUpdateGoals();
  const { updateBodyStatsAsync } = useUpdateBodyStats();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);

  const isLoading = isBodyStatsLoading || isGoalsLoading;

  if (isLoading) {
    return (
      <IonSkeletonText animated style={{ width: "100%", height: "200px", borderRadius: "32px" }} />
    );
  }

  const weight = bodyStats?.currentWeight ? Number(bodyStats.currentWeight) : 0;
  const weightUnit = bodyStats?.currentWeightUnit || "kg";

  const goalOptions = [
    { value: "lose-weight", name: "Lose weight" },
    { value: "gain-weight", name: "Gain weight" },
    { value: "maintain-weight", name: "Maintain weight" },
  ];

  const getGoalText = () => {
    if (!goals?.goalsWithNutriCoach) return "Goal not set";
    const goal = goals.goalsWithNutriCoach;
    const found = goalOptions.find((opt) => opt.value === goal);
    return found?.name || goal;
  };

  const getWeightText = () => {
    if (weight > 0) return `Current weight: ${weight} ${weightUnit}`;
    return "Weight not set";
  };

  const handleGoalUpdate = async (value: string) => {
    try {
      await updateGoalsAsync({
        goalsWithNutriCoach: value,
        specificWeightGoals: goals?.specificWeightGoals || "lose-weight",
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.GOALS] });
      showSuccess("Goal updated successfully");
    } catch (error) {
      showError("Failed to update goal");
      console.error(error);
    }
  };

  const handleWeightUpdate = async (value: string, unit: "kg" | "lbs") => {
    try {
      await updateBodyStatsAsync({
        height: bodyStats?.height || "170",
        heightUnit: bodyStats?.heightUnit || "cm",
        currentWeight: value,
        currentWeightUnit: unit,
        targetWeight: value,
        targetWeightUnit: unit,
        startingWeight: bodyStats?.startingWeight || value,
        startingWeightUnit: bodyStats?.startingWeightUnit || unit,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.BODY_STATS] });
      showSuccess("Weight updated successfully");
    } catch (error) {
      showError("Failed to update weight");
      console.error(error);
    }
  };

  return (
    <div
      className={cn(
        "w-full rounded-4xl border border-[#ededed] bg-white px-5 py-6",
        "shadow-[0px_8px_32.35px_rgba(64,0,255,0.1)]",
        className,
      )}
    >
      <div className="flex flex-col gap-6">
        <span className={cn(textVariants.title2, "w-full text-center font-bold")}>
          Your current goal is...
        </span>

        <div className="flex flex-col gap-3">
          <GoalInfoItem value={getGoalText()} onChange={() => setIsGoalModalOpen(true)} />

          <GoalInfoItem value={getWeightText()} onChange={() => setIsWeightModalOpen(true)} />
        </div>
      </div>

      <StandaloneGoalModal
        isOpen={isGoalModalOpen}
        onDismiss={() => setIsGoalModalOpen(false)}
        defaultValue={goals?.goalsWithNutriCoach || "lose-weight"}
        options={goalOptions}
        onSelect={handleGoalUpdate}
      />

      <WeightInputModal
        isOpen={isWeightModalOpen}
        onDismiss={() => setIsWeightModalOpen(false)}
        defaultValue={weight.toString()}
        defaultUnit={weightUnit}
        onSelect={handleWeightUpdate}
      />
    </div>
  );
});
